"use client";

import { ArrowUp, ChevronDown, ChevronRight, Mail, MapPin, Menu, Phone } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ReactNode, useRef, useState } from "react";
import { LocaleSwitcher } from "@/components/locale-switcher/locale-switcher";
import { BrandLogo } from "@/components/brand/brand-logo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { WideNavigationMenu } from "./wide-navigation-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import type { ResolvedFooter, ResolvedMenu, ResolvedNode } from "@/modules/site-layout/layout/resolve-site-layout";
import { DEFAULT_PANEL, type MenuPanel } from "@/modules/site-layout/layout/site-menu";
import { balanceColumns } from "@/modules/site-layout/layout/balance-columns";
import { footerColumnClass, footerLayoutClasses } from "@/modules/site-layout/layout/site-footer";
import { SOCIAL_NETWORKS, type SocialLink } from "@/lib/social-links/verify-social-links";
import { ON_BRAND, SiteIcon, SocialIcon, SoonTag } from "../button/landing-button";

/** One link in a drop-down panel: its picture (or icon tile), title and a short description; long words wrap inside the column instead of spilling out of it. */
function NavPanelLink({ node, panel, plain }: { node: ResolvedNode; panel: MenuPanel; plain?: boolean }) {
  const tile = node.image
    // eslint-disable-next-line @next/next/no-img-element -- menu pictures come from any address the owner enters
    ? <img src={node.image} alt="" loading="lazy" className="size-12 shrink-0 rounded-md object-cover" />
    : panel.icons && !node.noIcon && <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground"><SiteIcon name={node.icon} className="size-4" /></span>;
  const body = (
    <>
      {tile}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-medium break-words">{node.label}{node.soon && <SoonTag />}</span>
        {panel.descriptions && node.description && <span className="text-xs break-words text-muted-foreground">{node.description}</span>}
      </span>
    </>
  );
  if (node.soon) return <div className="flex min-w-0 items-start gap-3 p-3 opacity-80">{body}</div>;
  if (plain) return <a href={node.href} className="flex min-w-0 items-start gap-3 rounded-md p-3 hover:bg-accent">{body}</a>;
  return <NavigationMenuLink render={<a href={node.href} />} className="min-w-0 flex-row items-start gap-3 p-3">{body}</NavigationMenuLink>;
}

/** A group's heading and its entries; a folded group (`fold`) starts closed and opens with a button, so a long family stays short. */
function NavGroup({ node, panel, plain }: { node: ResolvedNode; panel: MenuPanel; plain?: boolean }) {
  const [open, setOpen] = useState(false);
  const heading = "px-3 pb-1 text-xs font-bold uppercase tracking-wider text-foreground";
  const list = <NavPanelList nodes={node.children ?? []} panel={panel} plain={plain} />;
  if (!node.fold) return <><p className={heading}>{node.label}</p>{list}</>;
  return (
    <>
      <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} className={`${heading} flex w-full items-center justify-between gap-2 rounded-md py-2 text-start hover:bg-accent`}>
        {node.label}
        <ChevronDown aria-hidden className={`size-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && list}
    </>
  );
}

/** An entry that opens a menu of its own, like a Start menu: its entries fly out beside it on hover or focus (touch: tap), over whatever is next to it. Flyouts nest. */
function NavSubmenuRow({ node, panel, plain }: { node: ResolvedNode; panel: MenuPanel; plain?: boolean }) {
  const row = useRef<HTMLDivElement>(null);
  const fly = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState({ flip: false, shift: 0 });
  /** Keeps the flyout on the screen: on the other side of its row when there is no room on the usual one, and moved up when it would pass the bottom. */
  const fit = () => {
    if (!row.current || !fly.current) return;
    const r = row.current.getBoundingClientRect();
    const { offsetWidth: w, offsetHeight: h } = fly.current;
    const rtl = getComputedStyle(row.current).direction === "rtl";
    const room = rtl ? r.left : window.innerWidth - r.right;
    const other = rtl ? window.innerWidth - r.right : r.left;
    const edge = 8;
    const shift = Math.max(edge - r.top, Math.min(0, window.innerHeight - edge - (r.top + h)));
    setPlace({ flip: room < w && other > room, shift });
  };
  return (
    <div ref={row} onMouseEnter={fit} onFocus={fit} className="group/fly relative">
      <button type="button" className="flex w-full min-w-0 items-start gap-3 rounded-md p-3 text-start hover:bg-accent group-hover/fly:bg-accent group-focus-within/fly:bg-accent">
        {panel.icons && !node.noIcon && <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground"><SiteIcon name={node.icon} className="size-4" /></span>}
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-medium break-words">{node.label}</span>
          {panel.descriptions && node.description && <span className="text-xs break-words text-muted-foreground">{node.description}</span>}
        </span>
        <ChevronRight aria-hidden className="mt-2 size-4 shrink-0 text-muted-foreground rtl:rotate-180" />
      </button>
      <div ref={fly} style={{ top: place.shift }} className={`invisible absolute z-20 opacity-0 ${place.flip ? "end-full pe-1" : "start-full ps-1"} group-focus-within/fly:visible group-focus-within/fly:opacity-100 group-hover/fly:visible group-hover/fly:opacity-100`}>
        <div className="w-72 rounded-md border bg-popover p-1 shadow-lg"><NavPanelList nodes={node.children ?? []} panel={panel} plain={plain} /></div>
      </div>
    </div>
  );
}

/** A list of links; an entry with its own entries is a submenu row that flies them out. It takes the width of the column it sits in. */
function NavPanelList({ nodes, panel, plain }: { nodes: ResolvedNode[]; panel: MenuPanel; plain?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      {nodes.map((node) => node.children
        ? <NavSubmenuRow key={node.id} node={node} panel={panel} plain={plain} />
        : <NavPanelLink key={node.id} node={node} panel={panel} plain={plain} />)}
    </div>
  );
}

/** The panel itself. With several submenus (families of offers) they sit side by side as columns (at most `panel.columns`; families are dealt into columns of about equal height by `balanceColumns`, and none is ever cut between two columns); the panel never grows past the screen, scrolls inside when tall, and never scrolls sideways. */
export function NavPanel({ nodes, panel, feature, promo, plain, intro }: { nodes: ResolvedNode[]; panel: MenuPanel; feature?: ResolvedNode["feature"]; promo?: ResolvedNode["promo"]; plain?: boolean; intro?: { title: string; text: string; image?: string } }) {
  const families = nodes.filter((n) => n.children).length;
  if (families < 2 && !feature && !promo && !intro) return <div className="w-[min(26rem,calc(100vw-2rem))] p-1"><NavPanelList nodes={nodes} panel={panel} plain={plain} /></div>;
  const columns = Math.max(1, Math.min(families, panel.columns));
  const runs = balanceColumns(nodes, columns);
  return (
    <div className={intro ? `flex max-h-[80vh] w-full flex-wrap gap-8 p-6 ${panel.full ? "" : "mx-auto max-w-[90rem]"}` : "flex max-h-[80vh] flex-wrap w-[min(var(--panel-width),calc(100vw-2rem))] gap-4 p-1"} style={{ "--panel-width": `${runs.length * 18 + (feature ? 17 : 0)}rem` } as React.CSSProperties}>
      {intro && (
        <div className="flex w-56 shrink-0 flex-col gap-3 border-e pe-8">
          {intro.image ? (
            <div className="relative h-36 overflow-hidden rounded-lg">
              {/* eslint-disable-next-line @next/next/no-img-element -- menu pictures come from any address the owner enters */}
              <img src={intro.image} alt="" loading="lazy" className="absolute inset-0 size-full object-cover" />
              <p className="absolute inset-x-0 bottom-0 bg-popover/90 px-3 py-2 text-lg leading-tight font-bold text-popover-foreground">{intro.title}</p>
            </div>
          ) : <p className="text-lg leading-tight font-bold">{intro.title}</p>}
          {intro.text && <p className="text-sm text-muted-foreground">{intro.text}</p>}
        </div>
      )}
      <div className="grid min-w-0 flex-1 items-start gap-x-2" style={{ gridTemplateColumns: `repeat(${runs.length}, minmax(0, 1fr))` }}>
        {runs.map((run, i) => (
          <div key={i} className="flex min-w-0 flex-col gap-4">
            {run.map((node) => node.children ? (
              <div key={node.id} className="flex min-w-0 flex-col gap-1">
                <NavGroup node={node} panel={panel} plain={plain} />
              </div>
            ) : <NavPanelLink key={node.id} node={node} panel={panel} plain={plain} />)}
          </div>
        ))}
      </div>
      {feature && <NavFeatureCard feature={feature} className={intro ? "h-80 w-60 self-start" : undefined} />}
      {promo && <NavPromoCard promo={promo} />}
    </div>
  );
}

/** The promo under the columns of a panel: the picture on one side, the title and text next to it, the button at the end. */
function NavPromoCard({ promo }: { promo: NonNullable<ResolvedNode["promo"]> }) {
  return (
    <div className="flex w-full flex-col items-stretch gap-4 overflow-hidden rounded-lg border bg-muted/40 sm:flex-row sm:items-center">
      {/* eslint-disable-next-line @next/next/no-img-element -- menu pictures come from any address the owner enters */}
      <img src={promo.image} alt="" loading="lazy" className="h-32 w-full shrink-0 object-cover sm:w-56" />
      <div className="flex min-w-0 flex-1 flex-col gap-1 px-4 pb-4 sm:py-3 sm:ps-0">
        {promo.title && <p className="text-lg leading-tight font-bold">{promo.title}</p>}
        {promo.text && <p className="text-sm text-muted-foreground">{promo.text}</p>}
      </div>
      {promo.label && <Button className="mx-4 mb-4 self-start sm:m-0 sm:me-4 sm:self-center" nativeButton={false} render={<a href={promo.href} />}>{promo.label}</Button>}
    </div>
  );
}

/** The featured card at the end of a panel: the picture fills it, the title and text sit over a dark fade at the bottom, with the button. */
function NavFeatureCard({ feature, className = "min-h-72 w-64" }: { feature: NonNullable<ResolvedNode["feature"]>; className?: string }) {
  return (
    <div className={`relative shrink-0 overflow-hidden rounded-lg ${className}`}>
      {/* eslint-disable-next-line @next/next/no-img-element -- menu pictures come from any address the owner enters */}
      <img src={feature.image} alt="" loading="lazy" className="absolute inset-0 size-full object-cover" />
      <div className="absolute inset-0 flex flex-col justify-end gap-2 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-4 text-white">
        {feature.title && <p className="text-lg leading-tight font-bold uppercase">{feature.title}</p>}
        {feature.text && <p className="text-sm text-white/85">{feature.text}</p>}
        {feature.label && <Button size="sm" variant="secondary" className="self-start" nativeButton={false} render={<a href={feature.href} />}>{feature.label}</Button>}
      </div>
    </div>
  );
}

/** The menu in the header bar on a wide screen: a single link, or a title that opens a panel of links and submenus. */
export function NavDesktopView({ nodes, label, panel = DEFAULT_PANEL }: { nodes: ResolvedNode[]; label: string; panel?: MenuPanel }) {
  const Menu = panel.wide ? WideNavigationMenu : NavigationMenu;
  return (
    <Menu aria-label={label} className="hidden md:flex">
      <NavigationMenuList>
        {nodes.map((node) => (
          <NavigationMenuItem key={node.id}>
            {node.children ? (
              <>
                <NavigationMenuTrigger>{node.label}</NavigationMenuTrigger>
                <NavigationMenuContent><NavPanel nodes={node.children} panel={panel} feature={node.feature} promo={node.promo} intro={panel.wide ? { title: node.label, text: node.description, image: node.image } : undefined} /></NavigationMenuContent>
              </>
            ) : <NavigationMenuLink render={<a href={node.href} />} className={navigationMenuTriggerStyle()}>{node.label}</NavigationMenuLink>}
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
    </Menu>
  );
}

/** The entries of the phone menu: a link, or an accordion item that opens its links (submenus open inside it). */
function NavMobileLinks({ nodes }: { nodes: ResolvedNode[] }) {
  return nodes.map((node) => node.children ? (
    <AccordionItem key={node.id} value={node.id}>
      <AccordionTrigger>{node.label}</AccordionTrigger>
      <AccordionContent className="flex flex-col gap-2 ps-3">
        <NavMobileLinks nodes={node.children} />
        {node.feature && <NavFeatureCard feature={node.feature} className="min-h-44 w-full" />}
        {node.promo && <NavPromoCard promo={node.promo} />}
      </AccordionContent>
    </AccordionItem>
  ) : node.soon ? <span key={node.id} className="py-2 text-muted-foreground">{node.label}<SoonTag /></span> : <a key={node.id} href={node.href} className="py-2 text-muted-foreground hover:text-foreground">{node.label}</a>);
}

/** The menu on a phone: a button that opens a sheet with the same entries (groups and submenus as accordions) and, at the bottom, the button of the menu (`children`). */
export function NavMobileView({ nodes, label, children }: { nodes: ResolvedNode[]; label: string; children?: ReactNode }) {
  return (
    <Sheet>
      <SheetTrigger render={<Button variant="ghost" size="icon" className="md:hidden" aria-label={label} />}><Menu /></SheetTrigger>
      <SheetContent side="left" className="gap-0 p-4">
        <SheetTitle className="sr-only">{label}</SheetTitle>
        <Accordion className="flex-1 overflow-y-auto pt-8"><NavMobileLinks nodes={nodes} /></Accordion>
        {children}
      </SheetContent>
    </Sheet>
  );
}

/** The owner's own button of the menu (its label and address); nothing when it has no label. */
export function SiteCtaView({ cta, size, className }: { cta: ResolvedMenu["cta"]; size?: "sm" | "default" | "lg"; className?: string }) {
  if (!cta.label) return null;
  return <Button size={size} className={className} nativeButton={false} render={<a href={cta.href} />}>{cta.label}</Button>;
}

/** Takes the visitor back to the top of the page. */
function FooterBackToTop() {
  const t = useTranslations("footer");
  return <Button type="button" variant="ghost" size="sm" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}><ArrowUp aria-hidden />{t("backToTop")}</Button>;
}

/** The contact lines of the brand block (each only when filled in): email, phone, postal address. */
function FooterContact({ brand }: { brand: ResolvedFooter["brand"] }) {
  const { email, phone, address } = brand;
  if (!email && !phone && !address) return null;
  return (
    <ul className="flex flex-col gap-2 text-sm text-brand-foreground/70">
      {email && <li className="flex items-center gap-2"><Mail className="size-4 shrink-0" aria-hidden /><a href={`mailto:${email}`} dir="ltr" className="hover:text-brand-foreground">{email}</a></li>}
      {phone && <li className="flex items-center gap-2"><Phone className="size-4 shrink-0" aria-hidden /><a href={`tel:${phone.replaceAll(" ", "")}`} dir="ltr" className="hover:text-brand-foreground">{phone}</a></li>}
      {address && <li className="flex items-start gap-2"><MapPin className="mt-0.5 size-4 shrink-0" aria-hidden /><span className="whitespace-pre-line">{address}</span></li>}
    </ul>
  );
}

/** The social network links, each drawn as the network's logo and named for screen readers. */
function FooterSocial({ links }: { links: SocialLink[] }) {
  if (links.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-3 text-brand-foreground/70">
      {links.map((link) => <li key={link.network}><a href={link.url} rel="noopener noreferrer" target="_blank" aria-label={SOCIAL_NETWORKS[link.network]} title={SOCIAL_NETWORKS[link.network]} className="block hover:text-brand-foreground"><SocialIcon network={link.network} className="size-5" /></a></li>)}
    </ul>
  );
}

/** The brand block: the logo (when on), the short description, the contact lines and the social links. */
function FooterBrand({ footer }: { footer: ResolvedFooter }) {
  const { brand, social } = footer;
  return (
    <div className="flex flex-col gap-4">
      {brand.logo && <BrandLogo className="h-9 w-auto self-start" />}
      {brand.description && <p className="max-w-xs text-sm text-brand-foreground/70">{brand.description}</p>}
      <FooterContact brand={brand} />
      <FooterSocial links={social} />
    </div>
  );
}

/** One footer column: its title and its links (a submenu inside a column is shown as a line of its own links under its name). */
function FooterLinkColumn({ node }: { node: ResolvedNode }) {
  return (
    <nav aria-label={node.label} className={`flex flex-col gap-3 ${footerColumnClass(node.width)}`}>
      <h3 className="text-sm font-semibold tracking-tight">{node.label}</h3>
      <ul className="flex flex-col gap-2 text-sm text-brand-foreground/70">
        {(node.children ?? []).flatMap((child) => child.children ?? [child]).map((link) => (
          <li key={link.id}><a href={link.href} className="inline-flex items-center gap-2 transition-colors hover:text-brand-foreground">{!link.noIcon && <SiteIcon name={link.icon} className="size-3.5 shrink-0 opacity-70" />}{link.label}</a></li>
        ))}
      </ul>
    </nav>
  );
}

/** The bottom bar: the copyright line, the legal links, and (when on) the language switcher and the back-to-top button. */
function FooterBottom({ bottom }: { bottom: ResolvedFooter["bottom"] }) {
  return (
    <div className="bg-black/25">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 px-6 py-5 text-xs text-brand-foreground/70 sm:flex-row">
        <p>{bottom.copyright}{bottom.legalLine && <><br />{bottom.legalLine}</>}</p>
        <ul className="flex flex-wrap items-center gap-4">
          {bottom.legal.map((link) => (
            <li key={link.id}><a href={link.href} className="underline underline-offset-4 hover:text-brand-foreground">{link.label}</a></li>
          ))}
          {bottom.languageSwitcher && <li><LocaleSwitcher /></li>}
          {bottom.backToTop && <li><FooterBackToTop /></li>}
        </ul>
      </div>
    </div>
  );
}

/** The foot of the public site: brand block and link columns (count, widths and brand position from the layout; extra columns wrap onto a new row), the big wordmark (when on) and the bottom bar. */
export function FooterView({ footer }: { footer: ResolvedFooter }) {
  const classes = footerLayoutClasses(footer.layout);
  return (
    <footer className={ON_BRAND}>
      <div className={`mx-auto max-w-6xl px-6 py-14 ${classes.outer}`}>
        <div className={classes.brand}><FooterBrand footer={footer} /></div>
        <div className={classes.links}>
          {footer.columns.map((node) => <FooterLinkColumn key={node.id} node={node} />)}
        </div>
      </div>
      {footer.brand.wordmark && <div aria-hidden className="mx-auto w-full max-w-6xl px-6 pt-4"><BrandLogo className="w-full opacity-20" /></div>}
      <FooterBottom bottom={footer.bottom} />
    </footer>
  );
}
